const net = require('net');
const { ErrorSubcode } = require('solclient-error');
const { Hex } = require('solclient-convert');
const { LOG_TRACE, LOG_DEBUG, LOG_WARN, LOG_ERROR } = require('solclient-log');
const { parseURL } = require('solclient-util');
const { TransportBase } = require('../transport-base');
const { TransportClientStats } = require('../transport-client-stats');
const { TransportError } = require('../transport-error');
const { TransportReturnCode } = require('../transport-return-codes');
const { TransportSessionEvent } = require('../transport-session-event');
const { TransportSessionEventCode } = require('../transport-session-event-codes');
const { TransportSessionState } = require('../transport-session-states');

const {
  formatHexString,
} = Hex;

/**
 * Tcp (tcps://) transport without TLS (tcps://) support.
 * Only works in node.js.
 * @private
 */
class TcpRawTransport extends TransportBase {

  constructor(url, eventCB, client, props) {
    // instanitate instance properties defined in TransportBase
    super(url, eventCB, client, props);

    /**
     * @private
     * @type {TransportClientStats}
     */
    this._clientstats = new TransportClientStats();

    /**
     * Maximum amount of send data than can be queued.
     * NodeJS socket.write accepts all writes, growing a
     * user space buffer indefinitely.
     * The TCP transport (same as WS transport)
     * manages the size of that buffer to stay below this limit.
     * @private
     * @type {Number}
     */
    this._sendBufferMaxSize = props.sendBufferMaxSize;

    /*
     * When true, the upper layer was told NO_SPACE and must be notified
     * of the next "drained" event.
     * @private
     * @type {Boolean}
     */
    this._canSendNeeded = false;

    /*
     * One-shot flush callback.
     * Set on flush(), ran and cleared on socket drain event.
     * @private
     */
    this._flushCallback = null;

    /**
     * Flag to keep track of socket drained state:
     * Set to false when socket.write returns false,
     * (indicating user-space buffering)
     * Set to true on socket drain events.
     * (indicating the user space buffer has flushed to the OS.)
     * @type {Boolean}
     * @private
     */
    this._drained = true;

    /**
     * @type {TransportSessionState}
     * @private
     */
    this._state = TransportSessionState.DOWN;

    /**
     * @type {?Socket}
     * @private
     */
    // Always the raw socket, which is stable from connect() until disconnect().
    this._rawSocket = null;

    /**
     * Save the Event in the onError listener
     * Use it in onClose to populate the event.
     * (Tcp socket calls onError with the error then onClose.
     * WS socket calls only onClose and passes the error directly.)
     * @private
     * @type {?Error}
     */
    this._socketError = null;

    /**
     * @type {?string}
     * @private
     */
    this._sessionId = new Date().getTime();

    // Some whitebox tests expect a transport->transportSession nesting.
    this._transportSession = this;
    this._props.transportProtocol = null;
  }

  /**
   * @override
   */
  getClientStats() {
    return this._clientstats;
  }
  setClientStats(value) {
    this._clientstats = value;
  }

  rehome(eventCB, client) {
    this._eventCB = eventCB;
    this._client = client;
  }

  /**
   * Extracts host and port from the router URL.
   * @private
   * @param {String} urlString the url
   * @returns {{String, Number}} host and port
   */
  parseHostPort(urlString) {
    let port = null;
    const urlParsed = parseURL(urlString);
    if (urlParsed.protocol !== 'tcp:' && urlParsed.protocol !== 'tcps:') {
      // Should not happen.
      LOG_ERROR(`Tcp Transport: URL with unexpected scheme: ${urlParsed.protocol}`);
    }
    const host = urlParsed.hostname;
    port = urlParsed.port;
    if (port === null) {
      if (this._ssl) {
        port = 55443;
      } else {
        port = this._props._compressionLevel > 0 ? 55003 : 55555;
      }
    }
    // The rest of the URL (path, etc) is ignored.
    LOG_TRACE(`Parsing url ${urlString} -> host: ${host} , port: ${port}`);
    return { host, port };
  }


  /**
   * Socket callback.
   * Bind the socket on setup to avoid stray errors from old sockets
   * in quick connect()-destroy()-connect() scenarios.
   * @private
   * @param {Socket} originalSocket the original socket
   * @param {Buffer} data the data
   */
  onData(originalSocket, data) {
    // We do not collect Rx stats.
    if (this._state === TransportSessionState.WAITING_FOR_DESTROY ||
      originalSocket !== this._rawSocket) {
      LOG_DEBUG('TcpTransportSession stray onData for previous socket, ignoring.');
      return;
    }
    if (this._client) {
      this._client.rxDataBuffer(data);
    }
  }

  /**
   * Socket callback.
   * Bind the socket on setup to avoid stray errors from old sockets
   * in quick connect()-destroy()-connect() scenarios.
   * @private
   * @param {Socket} originalSocket the original socket
   * @param {Error} error the error
   */
  onError(originalSocket, error) {
    if (originalSocket !== this._rawSocket) {
      LOG_DEBUG('TcpTransportSession stray onError for previous socket, ignoring.');
      return;
    }

    if (this._state === TransportSessionState.WAITING_FOR_DESTROY) {
      LOG_TRACE('Tcp transport is being destroyed, ignore error');
      return;
    }

    LOG_DEBUG(`Tcp transport connection error ${error}`);
    // net.socket invokes the error handler before the close handler.
    // Websocket does not, and that's what the upper layer expects.
    // Suppressing the "double tap" error events when connected,
    // saving the error for the onClose handler in _socketerror.
    if (this._state !== TransportSessionState.SESSION_UP) {
      if (this._state === TransportSessionState.WAITING_FOR_CREATE) {
        //There won't be an onClose invocation, so destroying right away.
        LOG_DEBUG(`Tcp transport connection error during establishment. ${error}`);
        this._state = TransportSessionState.CONNECTION_FAILED;
        this._destroy(originalSocket, `Connection failed. Message:${error.message}`, ErrorSubcode.CONNECTION_ERROR);
      } else {
        this._eventCB(
          new TransportSessionEvent(
            TransportSessionEventCode.SEND_ERROR,
            `Connection error${error}`,
            null,
            ErrorSubcode.CONNECTION_ERROR, null));
      }
    } else { // we rely on the close listener to notify the upper layer.
      this._socketError = error;
    }
  }

  /**
   * Socket callback.
   * Bind the socket argument to guard against stray invocations in a
   * quick connect()-destroy()-connect() scenario.
   * @private
   * @param {Socket} originalSocket original socket
   * @param {Boolean} hadError wheter there was an error
   */
  onClose(originalSocket, hadError) {
    if (originalSocket !== this._rawSocket) {
      LOG_DEBUG('TcpTransportSession stray onClose for previous socket, ignoring.');
      return;
    }

    if (this._state === TransportSessionState.WAITING_FOR_DESTROY) {
      LOG_TRACE('Tcp transport is being destroyed, ignore close');
      return;
    }
    const msg = hadError ? 'with error' : 'cleanly';
    const errorMsg = this._socketError && this._socketError.message;
    this._socketError = null; // clear on read.
    LOG_DEBUG(`Tcp transport connection is closed ${msg}. Message: ${errorMsg}`);
    this._state = TransportSessionState.CONNECTION_FAILED;

    this._destroy(this._rawSocket, `Connection closed ${msg}. Message:${errorMsg}`,
                  ErrorSubcode.COMMUNICATION_ERROR);
  }

  /**
   * Callback for Socket.connect.
   * Bind the socket argument to guard against stray invocations in a
   * quick connect()-destroy()-connect() scenario.
   * @private
   * @param {Socket} originalSocket original socket
   */
  onOpen(originalSocket) {
    LOG_TRACE('Plaintext transport received socket open event.');
    if (originalSocket !== this._rawSocket) {
      LOG_DEBUG('Plaintext transport stray onOpen for previous socket, ignoring.');
      return;
    }
    if (this._state !== TransportSessionState.WAITING_FOR_CREATE) {
      LOG_DEBUG(`TcpTransportSession stray onOpen when in state ${this._state}, ignoring.`);
      return;
    }

    this._state = TransportSessionState.SESSION_UP;
      // Send the event to the application letting it know that the session is up
    this._eventCB(
          new TransportSessionEvent(
              TransportSessionEventCode.UP_NOTICE,
              'Connected',
              0,
              null,
              this._sessionId));
  }

  /**
   * Establish underlying transport.
   * @override
   * @returns {TransportReturnCode} The result of this operation
   */
  connect() {
    if (this._state !== TransportSessionState.DOWN) {
      LOG_ERROR(`Invalid state for operation: ${TransportSessionState.nameOf(this._state)}`);
      return TransportReturnCode.INVALID_STATE_FOR_OPERATION;
    }
    if (this._rawSocket) {
      LOG_ERROR('TCP transport can not connect once _rawsocket exists.');
      return TransportReturnCode.INVALID_STATE_FOR_OPERATION;
    }
    if (!this._url) {
      LOG_WARN('Cannot connect to null URL');
      return TransportReturnCode.CONNECTION_ERROR;
    }

    LOG_DEBUG('Establishing Tcp transport session');
    try {
      this._state = TransportSessionState.WAITING_FOR_CREATE;
      const { host, port } = this.parseHostPort(this._url);
      LOG_DEBUG('Constructing socket');
      // Luckily raw and tls sockets have the same API.
      this._rawSocket = new net.Socket();
      this._rawSocket.on('connect', this.onOpen.bind(this, this._rawSocket));
      // Only connect after the connect listener is registered:
      this._rawSocket.connect(port, host);
      LOG_TRACE('Assigning properties to socket');
      // WARNING!
      // This is the kind of thing that breaks on minification.
      // Re-run unit tests in production mode when changed:
      // grunt --gruntfile=Gruntfile_Production.js
      // When broken, consult the closure compiler exceptions file:
      // (sdk-root)/builder/template/-common/env/externs.js
      this._rawSocket.on('data', this.onData.bind(this, this._rawSocket));
      this._rawSocket.on('error', this.onError.bind(this, this._rawSocket));
      this._rawSocket.on('close', this.onClose.bind(this, this._rawSocket));
      this._rawSocket.on('drain', this.onDrain.bind(this, this._rawSocket));
      this._rawSocket.setNoDelay(true);

      LOG_TRACE('Prepared socket');
    } catch (error) {
      LOG_DEBUG(`Error connecting: ${error}`);
      this._state = TransportSessionState.CONNECTION_FAILED;
      throw new TransportError(`Could not create Tcp socket: ${error.message}`,
        error.subcode || ErrorSubcode.CONNECTION_ERROR);
    }
    LOG_DEBUG('Tcp transport is connecting');

    return TransportReturnCode.OK;
  }

  /**
   * Destroy the underlying transport.
   * Returns transport return code.
   * @override
   * @param {?String} msg The message associated with this operation, if any.
   * @param {?ErrorSubcode} subcode The subcode associated with this operation, if any.
   * @returns {TransportReturnCode} The result of this operation
   */
  destroy(msg, subcode) {
    // No need to flush the decompressor,
    // the upper layer is not interested in any more data.
    return this._destroy(this._rawSocket, msg, subcode);
  }

  _destroy(originalSocket, msg, subcode) {
    if (originalSocket !== this._rawSocket) {
      LOG_DEBUG('TcpTransportSession stray _destroy for previous socket, ignoring.');
      return TransportReturnCode.OK;
    }
    if (this._state !== TransportSessionState.DOWN) {
      LOG_DEBUG(`Destroy Tcp transport: ${msg}`);
      // Set state for connection teardown.
      this._state = TransportSessionState.WAITING_FOR_DESTROY;

      const onerrorStub = function onerrorStub() { };
      if (this._rawSocket) {
        this._rawSocket.removeAllListeners();
        this._rawSocket.on('error', onerrorStub); // Prevent unhandled errors
        this._rawSocket.end();
        this._rawSocket = null;
      }

      this._canSendNeeded = false;
      this._flushCallback = null;
      this._drained = true;
      this._state = TransportSessionState.DOWN;
      this._client = null; // Don't accept data.
    }

    // always send a DESTROYED_NOTICE in response to a destroy() even
    // if we think the transport is already destroyed
    LOG_TRACE(`Destroying TCP transport. msg: ${msg} subcode: ${subcode}`);
    if (this._eventCB) {
      this._eventCB(new TransportSessionEvent(TransportSessionEventCode.DESTROYED_NOTICE,
                                              msg || 'Session is destroyed',
                                              null,
                                              subcode || 0,
                                              this._sessionId));
      // Release references to other components
      this._eventCB = null;
    }
    return TransportReturnCode.OK;
  }

  /**
   * Force fail the underlying transport's socket to force a failure.
   * Returns transport return code.
   * @override
   * @param {?String} msg The message associated with this operation, if any.
   * @returns {TransportReturnCode} The result of this operation
   */
  forceFailure(msg) {
    if (this._state !== TransportSessionState.DOWN) {
      const errorMsg = (msg !== undefined && msg !== null) ? msg : ''; // default empty
      LOG_DEBUG(`Destroy Tls transport: ${errorMsg}`);
      if (this._rawSocket) {
        this._rawSocket.destroy(new Error(errorMsg));
      }
    }
    return TransportReturnCode.OK;
  }

  /**
   * @override
   * @description Intended for clean shutdowns.
   * Callback is invoked when all nodejs-buffered data
   * has been flushed to the OS socket.
   */
  flush(callback) {
    if (this._drained) {
      LOG_DEBUG('Transport emitting FLUSH immediately.');
      callback();
    } else if (callback) {
      this._flushCallback = callback;
    }
    return TransportReturnCode.OK;
  }

  /**
   * @override
   * @description Send encoded SMF message
   * Returns transport return code.
   * @param {String} message The encoded SMF message to send
   * @param {?Boolean} [forceAllowEnqueue=false] If true, do not fail due to transport buffer full
   * @returns {TransportReturnCode} The result of this operation
   */
  send(message, forceAllowEnqueue = false) {
    if (this._state !== TransportSessionState.SESSION_UP) {
      LOG_ERROR('plaintext: INVALID_STATE_FOR_OPERATION');
      return TransportReturnCode.INVALID_STATE_FOR_OPERATION;
    }
    // Limiting socket buffer size to _sendBufferMaxSize unless
    // the force flag was set
    // or the socket is drained, in which case any size message goes.
    if (forceAllowEnqueue
      || this._drained
      || message.length < (this._sendBufferMaxSize - this._rawSocket.bufferSize)) {
      // _drained keeps track of "overflowing" writes.
      // nodejs never refuses a write but warns when it starts buffering.
      this._drained = this._rawSocket.write(message, 'ascii');
      if (this._clientstats) {
        this._clientstats.bytesWritten += message.length;
        ++this._clientstats.msgWritten;
      }
      return TransportReturnCode.OK;
    }
    // We'll unblock the user on the next onDrain.
    this._canSendNeeded = true;
    return TransportReturnCode.NO_SPACE;
  }

  /**
   * Socket callback.
   * Bind the socket on setup to avoid stray errors from old sockets
   * in quick connect()-destry()-connect() scenarios.
   * @private
   * @param {Socket} originalSocket the original socket
   */
  onDrain(originalSocket) {
    if (originalSocket !== this._rawSocket) {
      LOG_DEBUG('TcpTransportSession stray onDrain for previous socket, ignoring.');
      return;
    }
    this._drained = true;

    if (this._flushCallback) {
      LOG_DEBUG('Transport emitting FLUSH after drain.');
      const prevCallback = this._flushCallback;
      this._flushCallback = null;
      prevCallback();
    }

    if (this._canSendNeeded) {
      LOG_TRACE('Transport emitting CAN_ACCEPT_DATA');
      this._canSendNeeded = false;
      this._eventCB(
        new TransportSessionEvent(TransportSessionEventCode.CAN_ACCEPT_DATA,
                                  '', null, 0, this._sessionId));
    }
  }

  /**
   * @override
   */
  getInfoStr() {
    const str = `TcpTransportSession; sid=${formatHexString(this._sessionId)}`;
    return str;
  }

  pause() {
    if (this._rawSocket) {
      this._rawSocket.pause();
    }
  }

  unpause() {
    if (this._rawSocket) {
      this._rawSocket.resume();
    }
  }

}

module.exports.TcpRawTransport = TcpRawTransport;
